/* buildTypePopupConfig.js
 *
 * Per-build-type configuration for the BuildTypePopup. Each entry
 * maps the href of the chip (matched against `buildingTypes` in
 * data/mock.js) to a curated set of:
 *
 *   - matchers[]   contextual MiniMatcher launchers (category, label)
 *   - resources[]  internal guide pages with a friendly headline
 *   - explore      the "Read the full guide" deep-link (full info page)
 *
 * Keep this file lean. The popup falls back to a generic single-row
 * matcher + an "Explore this build type" link if a chip is missing
 * here, so we never have a dead chip click.
 */

export const BUILD_TYPE_POPUPS = {
  '/choosing-land': {
    eyebrow: 'Land & site',
    title: 'Choosing a block of land',
    subtitle: 'Get the block right before you fall in love with a builder.',
    matchers: [
      { category: 'professional:surveyor',       label: 'Find a Surveyor' },
      { category: 'professional:town_planner',   label: 'Find a Town Planner' },
      { category: 'professional:conveyancer',    label: 'Find a Conveyancer' },
    ],
    resources: [
      { title: 'Home & Land packages',    href: '/house-and-land-adelaide',         body: 'Live BuildPilot inventory of land + design bundles.' },
      { title: 'Block assessment guide',  href: '/choosing-land/block-assessment', body: 'Slope, services, overlays.' },
      { title: 'Site costs explained',    href: '/choosing-land/site-costs',       body: 'What blows out a build budget.' },
      { title: 'Adelaide soil types',     href: '/choosing-land/soil-types',       body: 'Reactive soil and what it costs.' },
    ],
    // Primary action button - appears above the guide link in the footer.
    // Used when there's a clearly transactional next step (browse listings
    // / browse packages) versus the more passive "read the guide" CTA.
    primaryAction: { label: 'Browse land for sale', href: '/land-for-sale-adelaide' },
    explore: { label: 'Read the choosing-land guide', href: '/choosing-land' },
  },

  '/building-a-new-home': {
    eyebrow: 'New build',
    title: 'Building a new home',
    subtitle: 'Personalised next steps for your new build, from block to keys.',
    matchers: [
      { category: 'professional:builder',           label: 'Find a New Home Builder' },
      { category: 'professional:building_designer', label: 'Find a Building Designer' },
      { category: 'professional:conveyancer',       label: 'Find a Conveyancer' },
    ],
    resources: [
      { title: 'Home & Land packages',   href: '/house-and-land-adelaide',                body: 'Live BuildPilot inventory of land + design bundles.' },
      { title: 'Build timeline',         href: '/building-a-new-home/build-timeline',    body: 'Stage-by-stage, week by week.' },
      { title: 'Choosing a builder',     href: '/building-a-new-home/choosing-a-builder', body: 'How to pick the right partner.' },
      { title: 'Contracts to watch',     href: '/building-a-new-home/contracts',         body: 'Clauses every owner should know.' },
      { title: 'House & land deep-dive', href: '/building-a-new-home/house-and-land-packages', body: 'When packages make sense.' },
    ],
    explore: { label: 'Read the new home guide', href: '/building-a-new-home' },
    primaryAction: { label: 'Search house designs', href: '/home-designs' },
  },

  '/renovations': {
    eyebrow: 'Renovation',
    title: 'Renovating your home',
    subtitle: 'Match with the right reno team and skip the rookie mistakes.',
    matchers: [
      { category: 'professional:renovation_builder', label: 'Find a Renovation Builder' },
      { category: 'professional:interior_designer',  label: 'Find an Interior Designer' },
      { category: 'professional:building_designer',  label: 'Find a Building Designer' },
    ],
    resources: [
      { title: 'Renovation costs',           href: '/renovations/costs',                    body: 'What 2026 reno budgets look like.' },
      { title: 'Choosing a renovator',       href: '/renovations/choosing-a-renovator',     body: 'Vetting checklist.' },
      { title: 'Planning a renovation',      href: '/renovations/planning',                 body: 'Permits, scope and sequencing.' },
      { title: 'Living through the build',   href: '/renovations/living-through-renovation', body: 'Sanity-saving logistics.' },
    ],
    explore: { label: 'Read the renovations guide', href: '/renovations' },
  },

  '/changing-builders': {
    eyebrow: 'Mid-build help',
    title: 'Changing builders',
    subtitle: 'Switch builders without losing your build or your deposit.',
    matchers: [
      { category: 'professional:renovation_builder', label: 'Find a Replacement Builder' },
      { category: 'professional:property_lawyer',    label: 'Find a Property Lawyer' },
      { category: 'partner:house_inspect',           label: 'Get a Builder Inspection' },
    ],
    resources: [
      { title: 'Builder went bankrupt',  href: '/builder-went-bankrupt-what-now', body: 'Immediate moves to protect yourself.' },
      { title: 'Disputes with builders', href: '/dispute-with-builder-adelaide',  body: 'How to escalate without losing time.' },
      { title: 'Common builder problems', href: '/common-builder-problems',       body: 'Red flags and remedies.' },
    ],
    explore: { label: 'Read the changing-builders guide', href: '/changing-builders' },
  },

  '/property-development': {
    eyebrow: 'Investment',
    title: 'Invest or develop',
    subtitle: 'Numbers, approvals and the right specialists for a profitable project.',
    matchers: [
      { category: 'professional:town_planner',     label: 'Find a Town Planner' },
      { category: 'professional:quantity_surveyor', label: 'Find a Quantity Surveyor' },
      { category: 'professional:civil_engineer',   label: 'Find a Civil Engineer' },
    ],
    resources: [
      { title: 'Adelaide growth suburbs', href: '/growth-suburbs',           body: 'Where capital growth is heading.' },
      { title: 'Adelaide build cost report', href: '/adelaide-build-cost-report', body: 'Current per-sqm benchmarks.' },
    ],
    explore: { label: 'Read the invest & develop guide', href: '/property-development' },
    primaryAction: { label: 'Browse home & land packages', href: '/house-and-land-adelaide' },
  },

  '/house-and-land-adelaide': {
    eyebrow: 'Turnkey investment',
    title: 'Home & Land Package',
    subtitle: 'Land + design bundled into one transaction. Browse live inventory or read the deep-dive first.',
    matchers: [
      { category: 'professional:broker',     label: 'Find a Mortgage Broker' },
      { category: 'professional:conveyancer', label: 'Find a Conveyancer' },
      { category: 'professional:surveyor',   label: 'Find a Surveyor' },
    ],
    resources: [
      { title: 'House & land deep-dive', href: '/building-a-new-home/house-and-land-packages', body: 'When packages make sense and what to watch.' },
      { title: 'Adelaide growth suburbs', href: '/growth-suburbs',                              body: 'Where capital growth is heading.' },
      { title: 'Adelaide build cost report', href: '/adelaide-build-cost-report-2026',         body: 'Current per-sqm benchmarks.' },
      { title: 'Choosing a builder',     href: '/building-a-new-home/choosing-a-builder',     body: 'How to pick the right partner.' },
    ],
    primaryAction: { label: 'Browse home & land packages', href: '/house-and-land-adelaide' },
    explore: { label: 'Read the home & land guide', href: '/building-a-new-home/house-and-land-packages' },
  },

  '/verandahs-pergolas': {
    eyebrow: 'Outdoor build',
    title: 'Verandahs & pergolas',
    subtitle: 'Outdoor entertaining additions, sorted properly.',
    matchers: [
      { category: 'professional:pergola_builder', label: 'Find a Pergola Builder' },
      { category: 'tradie:carpenter',             label: 'Find a Carpenter' },
    ],
    resources: [],
    explore: { label: 'Read the verandahs & pergolas guide', href: '/verandahs-pergolas' },
  },

  '/landscaping': {
    eyebrow: 'Landscaping',
    title: 'Landscaping your build',
    subtitle: 'Soft, hard and structural landscaping for new builds and renos.',
    matchers: [
      { category: 'tradie:landscaper', label: 'Find a Landscaper' },
      { category: 'tradie:concreter',  label: 'Find a Concreter' },
      { category: 'tradie:fencer',     label: 'Find a Fencing Contractor' },
    ],
    resources: [],
    explore: { label: 'Read the landscaping guide', href: '/landscaping' },
  },

  '/sheds-garages': {
    eyebrow: 'Outbuildings',
    title: 'Sheds & garages',
    subtitle: 'Detached structures, garages and storage solutions.',
    matchers: [
      { category: 'professional:shed_builder', label: 'Find a Shed Builder' },
      { category: 'tradie:concreter',          label: 'Find a Concreter' },
    ],
    resources: [],
    explore: { label: 'Read the sheds & garages guide', href: '/sheds-garages' },
  },

  '/decking': {
    eyebrow: 'Outdoor build',
    title: 'Decking',
    subtitle: 'Timber and composite decks built to last.',
    matchers: [
      { category: 'professional:deck_builder', label: 'Find a Deck Builder' },
      { category: 'tradie:carpenter',          label: 'Find a Carpenter' },
    ],
    resources: [],
    explore: { label: 'Read the decking guide', href: '/decking' },
  },

  '/interior-design': {
    eyebrow: 'Design',
    title: 'Interior design',
    subtitle: 'Selections, styling, colour palette and material specs.',
    matchers: [
      { category: 'professional:interior_designer', label: 'Find an Interior Designer' },
    ],
    resources: [],
    explore: { label: 'Read the interior design guide', href: '/interior-design' },
  },

  '/home-design': {
    eyebrow: 'Design',
    title: 'Home design',
    subtitle: 'Architects, designers and floor-plan inspiration.',
    matchers: [
      { category: 'professional:building_designer', label: 'Find a Building Designer' },
      { category: 'professional:interior_designer', label: 'Find an Interior Designer' },
    ],
    resources: [
      { title: 'Browse floor plans',  href: '/designs', body: 'Library of plans by bedroom count and lifestyle.' },
    ],
    explore: { label: 'Read the home design guide', href: '/home-design' },
    primaryAction: { label: 'Search house designs', href: '/home-designs' },
  },

  '/swimming-pools': {
    eyebrow: 'Outdoor build',
    title: 'Swimming pools',
    subtitle: 'Pool builds and integration with the rest of the home.',
    matchers: [
      { category: 'tradie:pool_builder', label: 'Find a Pool Builder' },
      { category: 'tradie:landscaper',   label: 'Find a Landscaper' },
    ],
    resources: [],
    explore: { label: 'Read the swimming pools guide', href: '/swimming-pools' },
  },
};

/** Lookup helper. Returns null when no config exists so callers can
 * decide whether to fall back to direct navigation. */
export function resolveBuildTypePopup(href) {
  if (!href) return null;
  return BUILD_TYPE_POPUPS[href] || null;
}
